import { useState } from "react";

import { Button } from "@/components/ui/button";
import type { Raspored } from "@/lib/gradilista";
import { procitajGreske } from "@/lib/greske";

interface BlokadaOverrideDialogProps {
  raspored: Raspored;
  onOdustani: () => void;
  onPotvrdi: (razlog: string) => Promise<void>;
}

export function BlokadaOverrideDialog({
  raspored,
  onOdustani,
  onPotvrdi,
}: BlokadaOverrideDialogProps) {
  const [razlog, setRazlog] = useState("");
  const [greska, setGreska] = useState<string | null>(null);
  const [sprema, setSprema] = useState(false);

  async function potvrdi(): Promise<void> {
    setSprema(true);
    setGreska(null);
    try {
      await onPotvrdi(razlog);
    } catch (error) {
      const poruke = procitajGreske(error);
      setGreska(
        poruke.polja.reason ??
          poruke.opcenito[0] ??
          "Odobrenje nije uspjelo.",
      );
    } finally {
      setSprema(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="odobrenje-naslov"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <section className="w-full max-w-xl rounded-[var(--radius-card)] bg-white p-6 shadow-xl">
        <h3
          id="odobrenje-naslov"
          className="text-[var(--text-section)] font-semibold"
        >
          Odobrenje iznimke
        </h3>
        <p className="mt-2">
          Odobravate iznimku za <strong>{raspored.radnik ?? "radnika"}</strong>{" "}
          na gradilištu <strong>{raspored.gradiliste ?? "nepoznato"}</strong>.
        </p>
        <div className="mt-4 rounded-[var(--radius-card)] bg-[var(--color-status-bad-bg)] p-3 text-[var(--color-status-bad-fg)]">
          <p className="font-medium">Razlozi nepodobnosti</p>
          <ul className="mt-1 list-disc pl-5">
            {(raspored.blocking_reasons ?? []).map((stavka) => (
              <li key={stavka}>{stavka}</li>
            ))}
          </ul>
        </div>
        <label className="mt-4 flex flex-col gap-2">
          <span className="oznaka">Razlog odobrenja ({razlog.length}/20)</span>
          <textarea
            value={razlog}
            onChange={(dogadaj) => setRazlog(dogadaj.target.value)}
            className="min-h-28 rounded-[var(--radius-control)] border border-[var(--color-input-border)] p-3"
          />
        </label>
        {greska ? (
          <p role="alert" className="mt-3 text-[var(--color-status-bad-fg)]">
            {greska}
          </p>
        ) : null}
        <div className="mt-5 flex justify-end gap-3">
          <Button variant="outline" onClick={onOdustani} disabled={sprema}>
            Odustani
          </Button>
          <Button
            disabled={razlog.trim().length < 20 || sprema}
            onClick={() => void potvrdi()}
          >
            {sprema ? "Odobravanje…" : "Potvrdi odobrenje"}
          </Button>
        </div>
      </section>
    </div>
  );
}
